import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";

export const err = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Something went wrong";
export const inp = "w-full h-10 rounded-lg bg-[#0d1424] border border-slate-800 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500";
export const card = "rounded-2xl border border-slate-800 bg-[#0B1020]";

export function HubShell({ children, testid }) {
  return <main className="min-h-screen bg-[#05070c] text-slate-100" data-testid={testid}><div className="max-w-[1200px] mx-auto px-4 py-6 space-y-6">{children}</div></main>;
}

export function Hero({ kicker, title, sub, image, primary, secondary, testid }) {
  return (
    <section className={`relative overflow-hidden ${card} min-h-[320px]`} data-testid={testid}>
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#05070c] via-[#05070c]/85 to-transparent" />
      <div className="relative p-6 md:p-10 max-w-xl">
        <p className="font-mono text-[11px] tracking-[0.3em] text-cyan-300">{kicker}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2 leading-[1.05]">{title}</h1>
        <p className="text-slate-300 mt-3 text-sm sm:text-base">{sub}</p>
        <div className="flex flex-wrap gap-2 mt-6">
          {primary && <Button onClick={primary.onClick} className="h-11 px-5 bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid={primary.testid}>{primary.label}<ArrowRight className="w-4 h-4 ml-2" /></Button>}
          {secondary && <Button variant="outline" onClick={secondary.onClick} className="h-11 px-5 border-blue-500/60 text-white bg-transparent hover:bg-blue-500/10" data-testid={secondary.testid}>{secondary.label}</Button>}
        </div>
      </div>
    </section>
  );
}

export function Section({ title, sub, right, children, testid, className = "", id }) {
  return (
    <section id={id} className={`${card} p-5 min-w-0 ${className}`} data-testid={testid}>
      {(title || right) && <div className="flex flex-wrap items-start justify-between gap-3 mb-4"><div><h2 className="font-display text-xl font-extrabold">{title}</h2>{sub && <p className="text-sm text-slate-400 mt-0.5">{sub}</p>}</div>{right}</div>}
      {children}
    </section>
  );
}

export function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="block text-xs text-slate-400 mb-1">{label}</span>{children}</label>;
}

export function Stepper({ steps, current, testid }) {
  const idx = steps.findIndex((s) => s.id === current);
  return (
    <ol className="flex flex-wrap items-center gap-2" data-testid={testid}>{steps.map((s, i) => <li key={s.id} className="flex items-center gap-2" data-testid={`${testid}-${s.id}`}><span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${i < idx ? "bg-emerald-400 text-slate-950" : i === idx ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-500"}`}>{i < idx ? "✓" : i + 1}</span><span className={`text-sm ${i <= idx ? "text-white" : "text-slate-500"}`}>{s.label}</span>{i < steps.length - 1 && <span className="hidden sm:block w-8 h-px bg-slate-700" />}</li>)}</ol>
  );
}

export function Badge({ children, tone = "slate", testid }) {
  const t = { slate: "bg-slate-800 text-slate-300", amber: "bg-amber-400/15 text-amber-300", blue: "bg-blue-600/20 text-blue-300", green: "bg-emerald-400/15 text-emerald-300", red: "bg-red-500/15 text-red-300" }[tone];
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t}`} data-testid={testid}>{children}</span>;
}

export function Tiles({ items, testid }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={testid}>{items.map((t) => <div key={t.title} className={`${card} p-4`} data-testid={`${testid}-${t.id || t.title}`}>{t.icon && <t.icon className="w-5 h-5 text-blue-400 mb-2" />}<p className="font-semibold">{t.title}</p><p className="text-sm text-slate-400 mt-1">{t.desc}</p>{t.to && <Link to={t.to} className="text-xs text-cyan-300 hover:underline mt-2 inline-block">{t.cta || "Learn more"} →</Link>}{t.onClick && <button onClick={t.onClick} className="text-xs text-cyan-300 hover:underline mt-2 inline-block" data-testid={`${testid}-${t.id || t.title}-cta`}>{t.cta} →</button>}</div>)}</div>;
}

export function Cta({ title, sub, label, onClick, testid }) {
  return <section className={`${card} p-6 md:p-8 flex flex-wrap items-center gap-6 bg-gradient-to-r from-[#0B1020] to-[#0f1a33]`} data-testid={testid}><div className="flex-1 min-w-[240px]"><h2 className="font-display text-2xl font-extrabold">{title}</h2><p className="text-slate-400 text-sm mt-1">{sub}</p></div><Button onClick={onClick} className="h-11 px-5 bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid={`${testid}-btn`}>{label}<ArrowRight className="w-4 h-4 ml-2" /></Button></section>;
}

export const Foot = ({ note }) => <p className="text-[11px] text-slate-600 text-center pb-4">{note}</p>;
